import React from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";

import ConfirmTooltip from "components/confirmTooltip/ConfirmTooltip";
import InnerInput from "components/inputs/InnerInput/InnerInput";

import styles from "./DeclineWithdrawalConfirm.module.scss";

export const OPERATOR_DECLINE_REASON_MAX_LENGTH = 200;

export const normalizeOperatorDeclineReason = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > OPERATOR_DECLINE_REASON_MAX_LENGTH) {
    return null;
  }
  return trimmed;
};

interface DeclineWithdrawalConfirmProps {
  withdrawalId: number;
  isOpen: boolean;
  isLoading: boolean;
  reason: string;
  onReasonChange: (value: string) => void;
  onToggle: (open: boolean) => void;
  onConfirm: () => void;
  children: React.ReactNode;
}

const DeclineWithdrawalConfirm: React.FC<DeclineWithdrawalConfirmProps> = ({
  withdrawalId,
  isOpen,
  isLoading,
  reason,
  onReasonChange,
  onToggle,
  onConfirm,
  children,
}) => {
  const { t } = useTranslation();
  const trimmedReason = reason.trim();
  const isReasonValid =
    trimmedReason.length > 0 &&
    trimmedReason.length <= OPERATOR_DECLINE_REASON_MAX_LENGTH;
  const tooltipId = `decline-withdrawal-${withdrawalId}`;

  return (
    <div data-tooltip-id={tooltipId}>
      {children}
      <ConfirmTooltip
        id={tooltipId}
        title={t("declineWithdrawal")}
        confirmText={t("confirmDecline")}
        cancelText={t("cancel")}
        isOpen={isOpen}
        isLoading={isLoading}
        confirmDisabled={!isReasonValid}
        closeOnConfirm={false}
        contentClassName={cn(styles.tooltip, styles.tooltipWide)}
        tooltipClassName={styles.tooltipRoot}
        onToggle={onToggle}
        onConfirm={onConfirm}
        config={{
          position: "left",
          positionStrategy: "fixed",
        }}
      >
        <div className={styles.reasonField}>
          <InnerInput
            id={`decline-withdrawal-reason-${withdrawalId}`}
            type="textarea"
            label={t("declineReason")}
            value={reason}
            rows={3}
            onChange={(value) =>
              onReasonChange(value.slice(0, OPERATOR_DECLINE_REASON_MAX_LENGTH))
            }
          />
          <span className={styles.charCounter}>
            {reason.length} / {OPERATOR_DECLINE_REASON_MAX_LENGTH}
          </span>
        </div>
      </ConfirmTooltip>
    </div>
  );
};

export default DeclineWithdrawalConfirm;
